import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { FileText, Download, Calendar, Building2, User, Mail, MapPin, Clock, CheckCircle, AlertCircle, CreditCard } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StatusBadge = ({ status }) => {
  const styles = {
    Paid: "bg-green-100 text-green-700 border-green-200",
    Pending: "bg-orange-100 text-orange-700 border-orange-200",
    Overdue: "bg-red-100 text-red-700 border-red-200"
  };

  const icons = {
    Paid: <CheckCircle className="w-3 h-3 mr-1" />,
    Pending: <Clock className="w-3 h-3 mr-1" />,
    Overdue: <AlertCircle className="w-3 h-3 mr-1" />
  };

  return (
    <Badge variant="outline" className={`${styles[status] || styles.Pending} text-sm flex items-center`}>
      {icons[status]}
      {status}
    </Badge>
  );
};

const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", CAD: "C$", AUD: "A$"
};

const PublicInvoiceView = () => {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const getCurrencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || "$";

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${API}/public/invoice/${invoiceId}`);
        setInvoice(res.data);
      } catch (err) {
        console.error("Error fetching invoice:", err);
        setError(err.response?.status === 404 ? "Invoice not found" : "Failed to load invoice");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [invoiceId]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/public/invoice/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded!");
    } catch (err) {
      console.error("Error downloading PDF:", err);
      toast.error("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  const handlePayInvoice = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.url;
      } else {
        toast.error("Unable to create payment session");
        setPaying(false);
      }
    } catch (err) {
      console.error("Error creating checkout:", err);
      const errorMsg = err.response?.data?.detail || "Failed to initiate payment";
      toast.error(errorMsg);
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <Skeleton className="h-12 w-48 mb-8" />
          <Card className="bg-white">
            <CardContent className="p-8 space-y-6">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
        <Card className="bg-white max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-xl font-bold text-foreground mb-2">{error}</h1>
            <p className="text-muted-foreground mb-6">
              The invoice you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild>
              <Link to="/">Go to Billuna</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Billuna
            </span>
          </Link>
          <StatusBadge status={invoice.status} />
        </div>

        {/* Invoice Card */}
        <Card className="bg-white invoice-paper mb-6" data-testid="public-invoice-card">
          <CardContent className="p-8">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-3xl font-bold text-primary mb-1">INVOICE</h1>
                <p className="text-lg text-muted-foreground font-mono">#{invoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end gap-2 text-muted-foreground mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Issued: {invoice.invoice_date}</span>
                </div>
                <div className={`flex items-center justify-end gap-2 ${invoice.status === 'Overdue' ? 'text-red-600' : 'text-muted-foreground'}`}>
                  <Clock className="w-4 h-4" />
                  <span>Due: {invoice.due_date}</span>
                </div>
              </div>
            </div>

            {/* From / To */}
            <div className="grid md:grid-cols-2 gap-8 mb-8 pb-8 border-b border-border">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  <Building2 className="w-4 h-4" />
                  From
                </div>
                <p className="font-semibold text-foreground text-lg mb-1">{invoice.business_name}</p>
                <p className="text-muted-foreground whitespace-pre-line">{invoice.business_address}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  <User className="w-4 h-4" />
                  Bill To
                </div>
                <p className="font-semibold text-foreground text-lg mb-1">{invoice.client_name}</p>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Mail className="w-4 h-4" />
                  {invoice.client_email}
                </div>
                {invoice.client_address && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    <span className="whitespace-pre-line">{invoice.client_address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <div className="grid grid-cols-12 gap-4 py-3 bg-muted rounded-lg px-4 mb-2">
                <div className="col-span-6 text-xs font-semibold text-muted-foreground uppercase">Description</div>
                <div className="col-span-2 text-xs font-semibold text-muted-foreground uppercase text-right">Qty</div>
                <div className="col-span-2 text-xs font-semibold text-muted-foreground uppercase text-right">Rate</div>
                <div className="col-span-2 text-xs font-semibold text-muted-foreground uppercase text-right">Amount</div>
              </div>
              {invoice.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 py-3 px-4 border-b border-border last:border-0">
                  <div className="col-span-6 font-medium">{item.description}</div>
                  <div className="col-span-2 text-right text-muted-foreground">{item.quantity}</div>
                  <div className="col-span-2 text-right font-mono">{getCurrencySymbol(invoice.currency)}{item.unit_price.toFixed(2)}</div>
                  <div className="col-span-2 text-right font-mono font-medium">{getCurrencySymbol(invoice.currency)}{item.total_price.toFixed(2)}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="font-mono">{getCurrencySymbol(invoice.currency)}{invoice.subtotal.toFixed(2)}</span>
                </div>
                {invoice.tax_percentage > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax ({invoice.tax_percentage}%)</span>
                    <span className="font-mono">{getCurrencySymbol(invoice.currency)}{invoice.tax_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-lg font-bold text-primary">Total Due</span>
                  <span className="text-lg font-bold text-primary font-mono">{getCurrencySymbol(invoice.currency)}{invoice.total.toFixed(2)} {invoice.currency}</span>
                </div>
              </div>
            </div>

            {/* Notes & Payment Instructions */}
            {(invoice.notes || invoice.payment_instructions) && (
              <div className="bg-muted rounded-lg p-6 space-y-4">
                {invoice.notes && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Notes</h3>
                    <p className="text-foreground whitespace-pre-line">{invoice.notes}</p>
                  </div>
                )}
                {invoice.payment_instructions && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Payment Instructions</h3>
                    <p className="text-foreground whitespace-pre-line">{invoice.payment_instructions}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {invoice.status !== "Paid" && (
            <Button 
              onClick={handlePayInvoice}
              disabled={paying}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="pay-invoice-btn"
            >
              {paying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay Invoice - {getCurrencySymbol(invoice.currency)}{invoice.total.toFixed(2)} {invoice.currency}
                </>
              )}
            </Button>
          )}
          
          <Button 
            onClick={handleDownloadPDF}
            disabled={downloading}
            size="lg"
            variant={invoice.status === "Paid" ? "default" : "outline"}
            className={invoice.status === "Paid" ? "bg-primary hover:bg-primary/90" : ""}
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Download PDF
              </>
            )}
          </Button>
        </div>

        {/* Payment Status Message */}
        {invoice.status === "Paid" && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <div className="flex items-center justify-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">This invoice has been paid. Thank you!</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-sm text-muted-foreground">
          <p>Powered by <Link to="/" className="text-primary hover:underline">Billuna</Link> – Simple invoicing. Faster payments.</p>
        </div>
      </div>
    </div>
  );
};

export default PublicInvoiceView;
